// @flow
import { detectExperiment, setExperiment } from '../../redux/modules/googleOptimize';
import {
    fetchBidderDataIfNeeded,
    getAuthToken,
    getBidderId,
    getMainhostLogin,
    getUserData,
    isBidderLoaded,
    isTermsOfServiceUnknown,
    isUserDataLoading,
    isUserLoggedIn,
    logOutBidder,
} from '../../redux/modules/user';
import {
    forceEnableNotificationsBannerDismiss,
    getForceEnableBanner,
    getPermission,
    requestBrowserPermission,
} from '../../redux/modules/notification';
import {
    getBaseUrl,
    getDeployment,
    getSailthruCustomerId,
    getStripePublishKey,
    getWWWBaseUrl,
} from '../../redux/modules/config';
import { getBrowserDataFromUA, getMobileBrowserOS, getUiBrowser, isUiMobile } from '../../redux/modules/browser';
import {
    getWhiteLabelLogo,
    getWhiteLabelName,
    getWhiteLabelSellerId,
    isWhiteLabel,
} from '../../redux/modules/whiteLabel';
import {
    openDownloadMobileModal,
    openFeedbackModal,
    openHealthcheckModal,
    openLoginModal,
    openSignUpModal,
} from '../../redux/modules/modal';
import React, { Component, type Node, StrictMode } from 'react';
import {
    recordAnalyticsForIdentify,
    trackPastAuctionResultsClick,
    trackPriceResultsClick,
} from '../../redux/modules/analytics';
import AppHeaderMeta from './AppHeaderMeta';
import appTheme from '../../theme/theme';
import Auth from './Auth';
import BackToTop from '../../components/BacktoTop/BackToTop';
import BannerOutdatedBrowser from '../../components/BannerOutdatedBrowser/BannerOutdatedBrowser';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Cookies from 'universal-cookie';
import CurrencyExperimentContainer from '../../components/ExperimentSetup/CurrencyExperimentContainer';
import CustomToastContainer from '../../components/Toast/CustomToastContainer';
import DismissableGetMobileBanner from '../../components/DismissableGetMobileBanner/DismissableGetMobileBanner';
import DismissableGetWebNotificationsBanner from '../../components/DismissableGetWebNotificationsBanner/DismissableGetWebNotificationsBanner';
import type { ExtractReturn } from '../../types/redux';
import FeedbackLink from '../../components/FeedbackLink/FeedbackLink';
import { fetchUpcomingBidderBiddingIfNeeded } from '../../redux/modules/bidderBiddingInfoUpcoming';
import Footer from '../../components/Footer/Footer';
import FullStory from '../../components/FullStory/FullStory';
import GeoLocationSearchExperimentContainer from '../../components/ExperimentSetup/GeoLocationSearchExperimentContainer';
import { getPageSizeFromCookie } from '../../utils/pagination';
import { getPlacedHighBid } from '../../redux/modules/biddingInfo';
import { getWindowSize } from '../../redux/modules/windowSize';
import type { GlobalState } from '../../redux/rootReducer';
import GoogleTagManager from '@liveauctioneers/caterwaul-components/lib/GoogleTagManager/GoogleTagManager';
import Header from '../../components/Header/Header';
import { hydrateClientState } from '../../redux/modules/client';
import isEqual from 'react-fast-compare';
import { loadScript } from '../../utils/script';
import Modals from '../../components/Modals/Modals';
import NotificationCountTitle from '../../components/NotificationCountTitle/NotificationCountTitle';
import { parseQueryParams } from '../../utils/queryParams';
import PreprodWarningBannerContainer from '../../components/PreprodWarningBanner/PreprodWarningBannerContainer';
import PubnubBidderNotificationContainer from '../../components/PubnubContainer/PubnubBidderNotificationContainer';
import PubnubGlobalNotificationContainer from '../../components/PubnubContainer/PubnubGlobalNotificationContainer';
// import RecaptchaScript from '../../components/Recaptcha/RecaptchaScript';
import SailthruInit from '../../components/Sailthru/SailthruInit';
import ScrollMemory from 'react-router-scroll-memory';
import SegmentTracker from '../../components/Segment/SegmentTracker';
import SoastaGlobalSetter from '../../components/Soasta/SoastaGlobalSetter';
import styles from './App.module.scss';
import { submitLogin } from '../../redux/modules/login';
import SuspendedBidderBannerContainer from '../../components/SuspendedBidderBanner/SuspendedBidderBannerContainer';
import TermsOfServiceBannerContainer from '../../components/TermsOfServiceBanner/TermsOfServiceBannerContainer';
import { ThemeProvider } from 'styled-components';
import WindowSizeContainer from '../../components/WindowSizeContainer/WindowSizeContainer';
import { withRouter } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

const cookie = new Cookies();

type OwnProps = $ReadOnly<{|
    history: any,
|}>;

const mapStateToProps = (state: GlobalState, { history }: OwnProps) => {
    const queryParams: any = parseQueryParams(history.location.search);
    const { pathname } = history.location;

    const authToken = getAuthToken(state);
    const baseUrl = getBaseUrl(state);
    const bidderIsLoaded = isBidderLoaded(state);
    const bidderId = getBidderId(state);
    const browser = getUiBrowser(state);
    const mainhostLogin = getMainhostLogin(state);
    const mobile = isUiMobile(state);
    const mobileBrowserOS = getMobileBrowserOS(state);
    const sailthruCustomerId = getSailthruCustomerId(state);
    const userData = getUserData(state);
    const userLoggedIn = isUserLoggedIn(state);
    const whiteLabel = isWhiteLabel(state);
    const whiteLabelName = getWhiteLabelName(state);
    const whiteLabelSellerId = getWhiteLabelSellerId(state);
    const logo = getWhiteLabelLogo(state);
    const biddingConsole = pathname.includes('/console/');
    const { width: windowWidth } = getWindowSize(state);
    const stripePublishKey = getStripePublishKey(state);
    const termsOfServiceUnknown = isTermsOfServiceUnknown(state);
    const userDataLoading = isUserDataLoading(state);
    const wwwBaseUrl = getWWWBaseUrl(state);
    const deployment = getDeployment(state);
    const permission = getPermission(state);
    const placedHighBid = getPlacedHighBid(state);
    const forceEnableBanner = getForceEnableBanner(state);

    // See if we are running the ItemCard bidding variant
    const disableModalForTesting = biddingConsole || queryParams.hideForTesting === 'true';

    return {
        authToken,
        baseUrl,
        bidderId,
        bidderIsLoaded,
        biddingConsole,
        browser,
        deployment,
        disableModalForTesting,
        forceEnableBanner,
        logo,
        mainhostLogin,
        mobile,
        mobileBrowserOS,
        pathname,
        permission,
        placedHighBid,
        queryParams,
        sailthruCustomerId,
        stripePublishKey,
        termsOfServiceUnknown,
        userData,
        userDataLoading,
        userLoggedIn,
        whiteLabel,
        whiteLabelName,
        whiteLabelSellerId,
        windowWidth,
        wwwBaseUrl,
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            detectExperiment,
            fetchBidderDataIfNeeded,
            fetchUpcomingBidderBiddingIfNeeded,
            forceEnableNotificationsBannerDismiss,
            hydrateClientState,
            logOutBidder,
            openDownloadMobileModal,
            openFeedbackModal,
            openHealthcheckModal,
            openLoginModal,
            openSignUpModal,
            recordAnalyticsForIdentify,
            requestBrowserPermission,
            setExperiment,
            submitLogin,
            trackPastAuctionResultsClick,
            trackPriceResultsClick,
        },
        dispatch,
    );

type ReduxProps = $ReadOnly<{|
    ...ExtractReturn<typeof mapStateToProps>,
    biddingConsole: boolean,
    browser: any,
    children: Node,
    disableModalForTesting: boolean,
    forceEnableBanner: boolean,
    logo: string,
    mobile: string,
    mobileBrowserOS: any,
    pathname: string,
    permission: string,
    placedHighBid: boolean,
    queryParams: any,
    recentItemIds: any[],
    whiteLabel: boolean,
    whiteLabelName: string,
    whiteLabelSellerId: number,
    windowWidth: number,
|}>;

type ReduxActions = $ReadOnly<{|
    detectExperiment: typeof detectExperiment,
    fetchBidderDataIfNeeded: typeof fetchBidderDataIfNeeded,
    fetchUpcomingBidderBiddingIfNeeded: typeof fetchUpcomingBidderBiddingIfNeeded,
    forceEnableNotificationsBannerDismiss: typeof forceEnableNotificationsBannerDismiss,
    hydrateClientState: typeof hydrateClientState,
    logOutBidder: typeof logOutBidder,
    openDownloadMobileModal: typeof openDownloadMobileModal,
    openFeedbackModal: typeof openFeedbackModal,
    openHealthcheckModal: typeof openHealthcheckModal,
    openLoginModal: typeof openLoginModal,
    openSignUpModal: typeof openSignUpModal,
    recordAnalyticsForIdentify: typeof recordAnalyticsForIdentify,
    requestBrowserPermission: typeof requestBrowserPermission,
    setExperiment: typeof setExperiment,
    submitLogin: typeof submitLogin,
    trackPastAuctionResultsClick: typeof trackPastAuctionResultsClick,
    trackPriceResultsClick: typeof trackPriceResultsClick,
|}>;

type Props = $ReadOnly<{|
    ...OwnProps,
    ...ReduxProps,
    ...ReduxActions,
|}>;

class App extends Component<Props> {
    static defaultProps = {
        authToken: '',
        disableModalForTesting: false,
        userLoggedIn: false,
    };

    componentDidMount() {
        // $FlowFixMe
        if (!__DEVELOPMENT__) {
            loadScript('https://cdn.polyfill.io/v2/polyfill.min.js?features=Intl.~locale.en', true);
        }

        this.hydrateClient().then(() => {
            this.props.fetchUpcomingBidderBiddingIfNeeded();
            this.props.fetchBidderDataIfNeeded(null, true);
            // detect whether we are running an experiment and set it
            this.props.detectExperiment();
        });

        this.getRequiredData();
    }

    shouldComponentUpdate = (nextProps: Props) => !isEqual(this.props, nextProps);

    componentDidUpdate() {
        const windowReference = window || global;
        if (typeof windowReference.performance !== 'undefined' && window.performance.mark) {
            // $FlowFixMe
            windowReference.performance?.mark('root-finished-update');
        }
    }

    componentWillUnmount() {
        // $FlowFixMe
        window.removeEventListener('storage', this.onStorage, false);
    }

    hydrateClient = async () => {
        const token = cookie.get('bidder-auth') || '';
        const authName = cookie.get('5Xq_name') || '';
        const authNuid = cookie.get('5Xq_nuid') || '';
        const authVerify = cookie.get('5Xq_nverify') || '';
        const dateLastShown = cookie.get('join-modal-last-seen') || '';
        const hasLoggedInBefore = Boolean(cookie.get('bidder-has-logged-in')) || Boolean(token);
        const pageSizeCookie = cookie.get('pagination-page-size') || 24;
        const pageSize = getPageSizeFromCookie(pageSizeCookie);

        const userAgent = navigator.userAgent || '';
        const browser = getBrowserDataFromUA(userAgent);
        const { disableModalForTesting } = this.props;

        await this.props.hydrateClientState({
            authName,
            authNuid,
            authVerify,
            browser,
            dateLastShown,
            disableModalForTesting,
            hasLoggedInBefore,
            pageSize,
            token,
        });
    };

    getRequiredData = () => {
        const { authToken, bidderIsLoaded, mainhostLogin } = this.props;

        if (!bidderIsLoaded) {
            if (!authToken && mainhostLogin.authVerify) {
                this.props.submitLogin(null, null, mainhostLogin);
            } else if (authToken) {
                this.props.fetchBidderDataIfNeeded(authToken, false);
            }
        }
    };

    render() {
        const {
            authToken,
            baseUrl,
            bidderId,
            biddingConsole,
            browser,
            // deployment,
            forceEnableBanner,
            forceEnableNotificationsBannerDismiss,
            logo,
            mainhostLogin,
            mobile,
            mobileBrowserOS,
            pathname,
            permission,
            placedHighBid,
            queryParams,
            requestBrowserPermission,
            sailthruCustomerId,
            termsOfServiceUnknown,
            trackPastAuctionResultsClick,
            trackPriceResultsClick,
            userData,
            userDataLoading,
            userLoggedIn,
            whiteLabel,
            whiteLabelName,
            whiteLabelSellerId,
            windowWidth,
            wwwBaseUrl,
        } = this.props;
        const showHeaderAndFooter = !biddingConsole;

        const isCheckoutPage = pathname.includes('checkout');
        const showFeedbackLink = !mobile && !whiteLabel && !isCheckoutPage;
        const showTOSBanner =
            userLoggedIn && termsOfServiceUnknown && !whiteLabel && !isCheckoutPage && !biddingConsole;
        const isWhiteLabel = Boolean(whiteLabel);

        const app = (
            <ThemeProvider theme={appTheme}>
                <ScrollMemory />
                <CurrencyExperimentContainer />
                <GeoLocationSearchExperimentContainer />
                <FullStory />
                {/* <RecaptchaScript deployment={deployment} /> */}
                {/* $FlowFixMe */}
                <FeedbackLink
                    showWidget={showFeedbackLink}
                    liveBidding={biddingConsole}
                    openFeedbackModal={this.props.openFeedbackModal}
                />
                {userLoggedIn && <PubnubBidderNotificationContainer bidderId={bidderId} />}
                {showTOSBanner && <TermsOfServiceBannerContainer />}
                {false && <PreprodWarningBannerContainer />}
                {!biddingConsole && (
                    <DismissableGetMobileBanner userLoggedIn={userLoggedIn} mobileBrowserOS={mobileBrowserOS} />
                )}
                <DismissableGetWebNotificationsBanner
                    forceEnableBanner={forceEnableBanner}
                    forceEnableNotificationsBannerDismiss={forceEnableNotificationsBannerDismiss}
                    permission={permission}
                    placedHighBid={placedHighBid}
                    requestBrowserPermission={requestBrowserPermission}
                />
                <SuspendedBidderBannerContainer isConsolePage={biddingConsole} />
                <CustomToastContainer />
                <PubnubGlobalNotificationContainer />
                <NotificationCountTitle />

                <div className={styles.app}>
                    {isWhiteLabel && <GoogleTagManager gtmId="GTM-MGNHR3N" />}
                    {isWhiteLabel && <GoogleTagManager gtmId="GTM-T68B3J7" />}
                    <WindowSizeContainer />
                    <AppHeaderMeta
                        baseUrl={baseUrl}
                        pathname={pathname}
                        whiteLabel={whiteLabel}
                        whiteLabelName={whiteLabelName}
                        whiteLabelSellerId={whiteLabelSellerId}
                        wwwBaseUrl={wwwBaseUrl}
                    />
                    <SailthruInit customerId={sailthruCustomerId} />
                    <Auth
                        authToken={authToken}
                        mainhostLogin={mainhostLogin}
                        recordAnalyticsForIdentify={this.props.recordAnalyticsForIdentify}
                        userData={userData}
                        userLoggedIn={userLoggedIn}
                    />
                    {browser.isOutdated && (
                        <BannerOutdatedBrowser name={browser.name} majorVersion={browser.majorVersion} />
                    )}
                    {showHeaderAndFooter && (
                        <Header
                            baseUrl={baseUrl}
                            biddingConsole={biddingConsole}
                            logOutBidder={this.props.logOutBidder}
                            logo={logo}
                            mobile={mobile}
                            openLoginModal={this.props.openLoginModal}
                            openSignUpModal={this.props.openSignUpModal}
                            pathname={pathname}
                            queryParams={queryParams}
                            trackPastAuctionResultsClick={trackPastAuctionResultsClick}
                            trackPriceResultsClick={trackPriceResultsClick}
                            userDataLoading={userDataLoading}
                            userFirstName={userData?.firstName}
                            userLoggedIn={userLoggedIn}
                            whiteLabel={whiteLabel}
                            windowWidth={windowWidth}
                        />
                    )}

                    <div className={styles.appContent} id="main">
                        {this.props.children}
                    </div>
                    {showHeaderAndFooter && <Footer whiteLabel={whiteLabel} />}
                    <Modals />
                    <SegmentTracker />
                    <SoastaGlobalSetter />
                    <BackToTop />
                </div>
            </ThemeProvider>
        );

        const testStrictMode = false;

        return testStrictMode ? <StrictMode>{app}</StrictMode> : app;
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
